import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useFrame } from '@react-three/fiber';
import { Mesh, Vector3, BoxHelper, Box3, MeshStandardMaterial } from 'three';
import { generatePositions } from './helpers';

// Feste Ziel-Bounding Box für alle Modelle
// Alle Modelle werden so skaliert, dass sie in diese Box passen
const TARGET_BOUNDING_BOX_SIZE = 2.0; // 2x2x2 Einheiten

const ModelSpawner = forwardRef(({
    model,
    groupRef,
    materials,
    minSpawnPositionX,
    minSpawnPositionY,
    minSpawnPositionZ,
    maxSpawnPositionX,
    maxSpawnPositionY,
    maxSpawnPositionZ,
    minDistanceBetweenObjects,
    minRotation,
    maxRotation,
    minObjects,
    maxObjects,
    randomizer = false, // Legacy support
    randomizePosition = false,
    randomizeRotation = false,
    isExporting = false, // Wird während Export gesetzt
    scale,
    modelUrl = null // URL des Modells, um zu prüfen ob es das Referenz-Modell ist
}, ref) => {
    const [objs, setObjs] = useState([]);
    const boundingBoxes = useRef([]);
    
    // Exponiere Objekte für Export
    useImperativeHandle(ref, () => ({
        getObjects: () => objs,
        getObjectCount: () => objs.length
    }));

    useEffect(() => {
        if (!model || !materials) {
            console.log('ModelSpawner: No model or materials', { model: !!model, materials: !!materials });
            setObjs([]);
            boundingBoxes.current = [];
            return;
        }

        console.log('ModelSpawner: Initializing objects for live preview', { 
            model: !!model, 
            materials: !!materials,
            minObjects,
            maxObjects,
            scale
        });

        try {
            // Berechne die Größe des Modells NACH dem Zentrieren
            // Dazu müssen wir zuerst eine Test-Geometrie klonen, zentrieren und dann die Bounding Box berechnen
            model.updateMatrixWorld(true);
            
            let maxDimension = 0;
            let hasValidGeometry = false;
            
            // Durchlaufe alle Meshes, um die maximale Dimension nach dem Zentrieren zu finden
            model.traverse(child => {
                if (child instanceof Mesh && child.geometry) {
                    try {
                        if (!child.geometry.attributes.position || child.geometry.attributes.position.count === 0) {
                            return;
                        }
                        
                        // Klone die Geometrie für die Berechnung
                        const testGeometry = child.geometry.clone();
                        
                        // Zentriere die Geometrie
                        testGeometry.center();
                        
                        // Berechne die Bounding Box der zentrierten Geometrie
                        testGeometry.computeBoundingBox();
                        
                        if (testGeometry.boundingBox) {
                            const size = testGeometry.boundingBox.getSize(new Vector3());
                            const localMax = Math.max(size.x, size.y, size.z);
                            maxDimension = Math.max(maxDimension, localMax);
                            hasValidGeometry = true;
                        }
                    } catch (error) {
                        console.warn('ModelSpawner: Error calculating geometry size:', error);
                    }
                }
            });
            
            if (!hasValidGeometry || maxDimension <= 0.001) {
                console.warn('ModelSpawner: Model has invalid dimensions or no valid geometry');
                setObjs([]);
                boundingBoxes.current = [];
                return;
            }
            
            // Skaliere das Modell so, dass es in die feste Ziel-Bounding Box passt
            // Die größte Dimension des zentrierten Modells soll genau TARGET_BOUNDING_BOX_SIZE sein
            const finalScale = TARGET_BOUNDING_BOX_SIZE / maxDimension;
            
            console.log(`ModelSpawner: Max dimension (after centering): ${maxDimension.toFixed(4)}, Target box size: ${TARGET_BOUNDING_BOX_SIZE}, Scale factor: ${finalScale.toFixed(4)}`);

            const objectCount = Math.floor(Math.random() * (maxObjects - minObjects + 1)) + minObjects;
            const temporaryObjs = [];
            const temporaryBoundingBoxes = [];
            let meshCount = 0;

            model.traverse(child => {
                if (child instanceof Mesh && child.geometry) {
                    meshCount++;
                    try {
                        // Prüfe ob Geometrie gültig ist
                        if (!child.geometry.attributes.position || child.geometry.attributes.position.count === 0) {
                            console.warn(`ModelSpawner: Mesh ${meshCount} has empty geometry, skipping`);
                            return;
                        }
                        
                        // Erstelle eine Kopie der Geometrie, um die Original-Geometrie nicht zu modifizieren
                        const geometry = child.geometry.clone();
                        if (!geometry.attributes.position || geometry.attributes.position.count === 0) {
                            console.warn(`ModelSpawner: Cloned geometry ${meshCount} is empty, skipping`);
                            return;
                        }
                        
                        // WICHTIG: Zentriere die Geometrie zuerst
                        geometry.center();
                        
                        // Wende die Skalierung an - alle Objekte werden auf die Referenzgröße skaliert
                        // Verwende geometry.scale() für zuverlässige Skalierung
                        geometry.scale(finalScale, finalScale, finalScale);
                        
                        // Stelle sicher, dass die Geometrie nach Skalierung aktualisiert wird
                        geometry.computeBoundingBox();
                        geometry.computeBoundingSphere();
                        
                        // Stelle sicher, dass die Position-Attribute aktualisiert werden
                        if (geometry.attributes.position) {
                            geometry.attributes.position.needsUpdate = true;
                        }
                        
                        // Validiere die Skalierung - prüfe die neue Größe und ob alle Punkte in der Box liegen
                        geometry.computeBoundingBox();
                        if (geometry.boundingBox) {
                            const scaledSize = geometry.boundingBox.getSize(new Vector3());
                            const scaledMax = Math.max(scaledSize.x, scaledSize.y, scaledSize.z);
                            const min = geometry.boundingBox.min;
                            const max = geometry.boundingBox.max;
                            
                            // Prüfe ob alle Punkte innerhalb der Ziel-Bounding Box liegen
                            const halfBox = TARGET_BOUNDING_BOX_SIZE / 2;
                            const fitsInBox = Math.abs(min.x) <= halfBox && Math.abs(max.x) <= halfBox &&
                                             Math.abs(min.y) <= halfBox && Math.abs(max.y) <= halfBox &&
                                             Math.abs(min.z) <= halfBox && Math.abs(max.z) <= halfBox;
                            
                            console.log(`ModelSpawner: Mesh ${meshCount} scaled to: ${scaledMax.toFixed(4)} (target: ${TARGET_BOUNDING_BOX_SIZE}), fits in box: ${fitsInBox}`);
                            
                            if (!fitsInBox) {
                                console.warn(`ModelSpawner: Mesh ${meshCount} does not fit in bounding box! Bounds: [${min.x.toFixed(2)}, ${min.y.toFixed(2)}, ${min.z.toFixed(2)}] to [${max.x.toFixed(2)}, ${max.y.toFixed(2)}, ${max.z.toFixed(2)}]`);
                            }
                        }
                        
                        // Prüfe ob Geometrie nach Skalierung noch gültig ist
                        if (!geometry.attributes.position || geometry.attributes.position.count === 0) {
                            console.warn(`ModelSpawner: Geometry ${meshCount} became empty after scaling, skipping`);
                            return;
                        }
                        
                        const positions = generatePositions(
                            objectCount,
                            new Vector3(minSpawnPositionX, minSpawnPositionY, minSpawnPositionZ),
                            new Vector3(maxSpawnPositionX, maxSpawnPositionY, maxSpawnPositionZ),
                            minDistanceBetweenObjects
                        );

                        console.log(`ModelSpawner: Processing mesh ${meshCount}, spawning ${positions.length} objects with final scale ${finalScale.toFixed(4)}, geometry vertices: ${geometry.attributes.position.count}`);

                        for (let i = 0; i < positions.length; i++) {
                            const pos = positions[i];
                            if (!pos) continue;
                            
                            // Erstelle ein neues Mesh mit der skalierten Geometrie
                            const clonedGeometry = geometry.clone();
                            if (!clonedGeometry.attributes.position || clonedGeometry.attributes.position.count === 0) {
                                console.warn(`ModelSpawner: Cloned geometry for object ${i} is empty, skipping`);
                                continue;
                            }
                            
                            const clone = new Mesh(clonedGeometry);
                            
                            // Setze Position und Rotation
                            clone.position.set(pos.x, pos.y, pos.z);
                            clone.rotation.set(
                                Math.random() * (maxRotation - minRotation) + minRotation,
                                Math.random() * (maxRotation - minRotation) + minRotation,
                                Math.random() * (maxRotation - minRotation) + minRotation
                            );
                            
                            // Verwende das PBR-Material (Index 1), nicht das Basic-Material (Index 0)
                            const materialToUse = Array.isArray(materials) ? materials[1] || materials[0] : materials;
                            if (!materialToUse) {
                                console.warn('ModelSpawner: No material available, using fallback material');
                                clone.material = new MeshStandardMaterial({
                                    color: 0xffffff,
                                    emissive: 0xffffff,
                                    emissiveIntensity: 0.5,
                                    visible: true
                                });
                            } else {
                                const clonedMaterial = materialToUse.clone();
                                clonedMaterial.visible = true;
                                clonedMaterial.transparent = false;
                                clonedMaterial.opacity = 1.0;
                                if (clonedMaterial.emissive) {
                                    clonedMaterial.emissive.setHex(0xffffff);
                                }
                                clonedMaterial.emissiveIntensity = Math.max(clonedMaterial.emissiveIntensity || 0, 0.2);
                                clonedMaterial.needsUpdate = true;
                                clone.material = clonedMaterial;
                            }
                            
                            // Stelle sicher, dass das Objekt sichtbar ist
                            clone.visible = true;
                            clone.castShadow = true;
                            clone.receiveShadow = true;
                            
                            // Stelle sicher, dass die Geometrie aktualisiert wird
                            clonedGeometry.computeBoundingBox();
                            clonedGeometry.computeBoundingSphere();
                            
                            // Stelle sicher, dass die Matrix aktualisiert wird
                            clone.updateMatrix();
                            clone.updateMatrixWorld(true);
                            
                            // WICHTIG: Stelle sicher, dass das Objekt in der Szene gerendert wird
                            // Füge das Objekt explizit zur Szene hinzu (wird durch primitive automatisch gemacht, aber sicherstellen)
                            clone.matrixAutoUpdate = true;
                            
                            temporaryObjs.push(clone);

                            // Create bounding box helper
                            const boundingBox = new BoxHelper(clone);
                            temporaryBoundingBoxes.push(boundingBox);
                        }
                    } catch (error) {
                        console.error(`Error processing mesh ${meshCount}:`, error);
                    }
                }
            });

            console.log(`ModelSpawner: Created ${temporaryObjs.length} objects from ${meshCount} meshes`);
            setObjs(temporaryObjs);
            boundingBoxes.current = temporaryBoundingBoxes;
        } catch (error) {
            console.error('Error spawning model:', error);
            setObjs([]);
            boundingBoxes.current = [];
        }
    }, [model, materials, minSpawnPositionX, minSpawnPositionY, minSpawnPositionZ, maxSpawnPositionX, maxSpawnPositionY, maxSpawnPositionZ, minDistanceBetweenObjects, minRotation, maxRotation, minObjects, maxObjects, scale, modelUrl]);
    
    // Aktualisiere Materialien für alle bereits erstellten Objekte, wenn sich materials ändert
    useEffect(() => {
        if (!materials || objs.length === 0) return;
        
        const materialToUse = Array.isArray(materials) ? materials[1] || materials[0] : materials;
        if (!materialToUse) return;
        
        objs.forEach(obj => {
            if (obj && obj.material) {
                // Kopiere Material-Eigenschaften vom neuen Material
                if (Array.isArray(obj.material)) {
                    obj.material.forEach((mat, index) => {
                        if (mat && materialToUse) {
                            // Kopiere spezifische Material-Eigenschaften
                            if (materialToUse.color && mat.color) mat.color.copy(materialToUse.color);
                            if (materialToUse.emissive && mat.emissive) mat.emissive.copy(materialToUse.emissive);
                            if (typeof materialToUse.metalness !== 'undefined') mat.metalness = materialToUse.metalness;
                            if (typeof materialToUse.roughness !== 'undefined') mat.roughness = materialToUse.roughness;
                            if (typeof materialToUse.clearcoat !== 'undefined') mat.clearcoat = materialToUse.clearcoat;
                            if (typeof materialToUse.clearcoatRoughness !== 'undefined') mat.clearcoatRoughness = materialToUse.clearcoatRoughness;
                            if (typeof materialToUse.reflectivity !== 'undefined') mat.reflectivity = materialToUse.reflectivity;
                            if (typeof materialToUse.transmission !== 'undefined') mat.transmission = materialToUse.transmission;
                            if (typeof materialToUse.emissiveIntensity !== 'undefined') mat.emissiveIntensity = materialToUse.emissiveIntensity;
                            
                            // Kopiere Texturen
                            if (materialToUse.map) mat.map = materialToUse.map;
                            if (materialToUse.normalMap) mat.normalMap = materialToUse.normalMap;
                            if (materialToUse.roughnessMap) mat.roughnessMap = materialToUse.roughnessMap;
                            if (materialToUse.metalnessMap) mat.metalnessMap = materialToUse.metalnessMap;
                            if (materialToUse.aoMap) mat.aoMap = materialToUse.aoMap;
                            if (materialToUse.emissiveMap) mat.emissiveMap = materialToUse.emissiveMap;
                            
                            mat.needsUpdate = true;
                        }
                    });
                } else {
                    if (materialToUse) {
                        // Kopiere spezifische Material-Eigenschaften
                        if (materialToUse.color && obj.material.color) obj.material.color.copy(materialToUse.color);
                        if (materialToUse.emissive && obj.material.emissive) obj.material.emissive.copy(materialToUse.emissive);
                        if (typeof materialToUse.metalness !== 'undefined') obj.material.metalness = materialToUse.metalness;
                        if (typeof materialToUse.roughness !== 'undefined') obj.material.roughness = materialToUse.roughness;
                        if (typeof materialToUse.clearcoat !== 'undefined') obj.material.clearcoat = materialToUse.clearcoat;
                        if (typeof materialToUse.clearcoatRoughness !== 'undefined') obj.material.clearcoatRoughness = materialToUse.clearcoatRoughness;
                        if (typeof materialToUse.reflectivity !== 'undefined') obj.material.reflectivity = materialToUse.reflectivity;
                        if (typeof materialToUse.transmission !== 'undefined') obj.material.transmission = materialToUse.transmission;
                        if (typeof materialToUse.emissiveIntensity !== 'undefined') obj.material.emissiveIntensity = materialToUse.emissiveIntensity;
                        
                        // Kopiere Texturen
                        if (materialToUse.map) obj.material.map = materialToUse.map;
                        if (materialToUse.normalMap) obj.material.normalMap = materialToUse.normalMap;
                        if (materialToUse.roughnessMap) obj.material.roughnessMap = materialToUse.roughnessMap;
                        if (materialToUse.metalnessMap) obj.material.metalnessMap = materialToUse.metalnessMap;
                        if (materialToUse.aoMap) obj.material.aoMap = materialToUse.aoMap;
                        if (materialToUse.emissiveMap) obj.material.emissiveMap = materialToUse.emissiveMap;
                        
                        obj.material.needsUpdate = true;
                    }
                }
            }
        });
    }, [materials]);

    useFrame(() => {
        // Randomizer nur während Export aktivieren
        const shouldRandomize = isExporting && (randomizer || randomizePosition || randomizeRotation);
        
        if (shouldRandomize) {
            // Randomisiere Position wenn aktiviert
            if (randomizer || randomizePosition) {
                const newPositions = generatePositions(
                    objs.length,
                    new Vector3(minSpawnPositionX, minSpawnPositionY, minSpawnPositionZ),
                    new Vector3(maxSpawnPositionX, maxSpawnPositionY, maxSpawnPositionZ),
                    minDistanceBetweenObjects
                );

                for (let i = 0; i < objs.length; i++) {
                    const pos = newPositions[i];
                    const obj = objs[i];
                    if (pos && obj) {
                        obj.position.set(pos.x, pos.y, pos.z);
                        
                        // Update bounding box position
                        const boundingBox = boundingBoxes.current[i];
                        if (boundingBox) boundingBox.setFromObject(obj);
                    }
                }
            }

            // Randomisiere Rotation wenn aktiviert
            if (randomizer || randomizeRotation) {
                for (let i = 0; i < objs.length; i++) {
                    const obj = objs[i];
                    if (obj) {
                        obj.rotation.set(
                            Math.random() * (maxRotation - minRotation) + minRotation,
                            Math.random() * (maxRotation - minRotation) + minRotation,
                            Math.random() * (maxRotation - minRotation) + minRotation
                        );
                        
                        // Update bounding box
                        const boundingBox = boundingBoxes.current[i];
                        if (boundingBox) boundingBox.setFromObject(obj);
                    }
                }
            }
        }
    });

    console.log('ModelSpawner: Rendering', objs.length, 'objects');

    // Stelle sicher, dass alle Objekte sichtbar sind und Materialien haben
    useEffect(() => {
        objs.forEach((obj, i) => {
            if (obj) {
                obj.visible = true;
                if (obj.material) {
                    // Stelle sicher, dass das Material sichtbar ist
                    if (Array.isArray(obj.material)) {
                        obj.material.forEach(mat => {
                            if (mat) {
                                mat.visible = true;
                                mat.transparent = false;
                                mat.opacity = 1.0;
                                // Stelle sicher, dass Emissive gesetzt ist
                                if (!mat.emissive || mat.emissiveIntensity === 0) {
                                    if (mat.emissive) {
                                        mat.emissive.setHex(0xffffff);
                                    }
                                    mat.emissiveIntensity = 0.2;
                                }
                                mat.needsUpdate = true;
                            }
                        });
                    } else {
                        obj.material.visible = true;
                        obj.material.transparent = false;
                        obj.material.opacity = 1.0;
                        // Stelle sicher, dass Emissive gesetzt ist
                        if (!obj.material.emissive || obj.material.emissiveIntensity === 0) {
                            if (obj.material.emissive) {
                                obj.material.emissive.setHex(0xffffff);
                            }
                            obj.material.emissiveIntensity = 0.2;
                        }
                        obj.material.needsUpdate = true;
                    }
                } else {
                    // Kein Material vorhanden - erstelle ein sichtbares Material
                    console.warn(`ModelSpawner: Object ${i} has no material, creating fallback`);
                    obj.material = new MeshStandardMaterial({
                        color: 0xffffff,
                        emissive: 0xffffff,
                        emissiveIntensity: 0.5,
                        visible: true
                    });
                }
            }
        });
    }, [objs]);

    if (objs.length === 0) {
        console.warn('ModelSpawner: No objects to render');
        return null;
    }

    return (
        <group ref={groupRef}>
            {objs.map((obj, i) => {
                if (!obj) {
                    console.warn(`ModelSpawner: Object at index ${i} is null`);
                    return null;
                }
                // Stelle sicher, dass das Objekt sichtbar ist und gerendert wird
                if (obj.visible === false) {
                    obj.visible = true;
                }
                if (obj.material) {
                    if (Array.isArray(obj.material)) {
                        obj.material.forEach(mat => {
                            if (mat) mat.visible = true;
                        });
                    } else {
                        obj.material.visible = true;
                    }
                }
                return <primitive key={`obj-${i}`} object={obj} />;
            })}
        </group>
    );
});

ModelSpawner.displayName = 'ModelSpawner';

export default ModelSpawner;
